import { Button, Glyphicon, OverlayTrigger, Table } from 'react-bootstrap';
import { IAggregatedMonth } from '@nkr/store/microsites/hooks/use-coach-performance-report';
import AssignmentsByCenterModal from './assignments-by-center-modal';
import NewInactiveByReasonModal from './new-inactive-by-reason-modal';
import { MONTH_NAMES } from './constants';
import { styles } from './styles';
import {
    NewAssignmentsTooltip,
    TotalActivePatientsTooltip,
    ActiveCustomSitesTooltip,
    ActiveStarterSitesTooltip,
    NewInactiveTooltip,
    LivingDonorTxTooltip,
    NewMsRegistrationTooltip,
    OptInPercentTooltip,
} from './tooltips';

interface IProps {
    data: IAggregatedMonth[];
    isSuperAdmin: boolean;
    year: number;
    coachId: number;
}


const info = (tooltip: JSX.Element) => (
    <OverlayTrigger trigger={['hover', 'focus']} placement='right' overlay={tooltip}>
        <Glyphicon glyph='info-sign' style={styles.infoIcon} />
    </OverlayTrigger>
);

const PerformanceGrid = ({ data, isSuperAdmin, year, coachId }: IProps) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    const isCurrentMonth = (m: IAggregatedMonth) =>
        m.year === currentYear && m.month === currentMonth;

    const headerStyle = (m: IAggregatedMonth) =>
        isCurrentMonth(m) ? styles.headerCellActive : styles.headerCell;

    return (
        <>
        <div style={styles.gridWrapper}>
            <Table striped condensed bordered>
                <thead>
                    <tr>
                        <th style={styles.headerCell}></th>
                        {data.map(m => (
                            <th key={m.key} style={headerStyle(m)}>
                                {MONTH_NAMES[m.month - 1]} {m.year}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={styles.labelCell}>
                            New Assignments {info(NewAssignmentsTooltip)}
                            <AssignmentsByCenterModal isSuperAdmin={isSuperAdmin} year={year} coachId={coachId} />
                        </td>
                        {data.map(m => <td key={m.key} style={styles.dataCell}>{m.newAssignments}</td>)}
                    </tr>
                    <tr>
                        <td style={styles.labelCell}>Total Active Patients* {info(TotalActivePatientsTooltip)}</td>
                        {data.map(m => <td key={m.key} style={styles.dataCell}>{m.totalActivePatients}</td>)}
                    </tr>
                    <tr>
                        <td style={styles.labelCell}>Active Custom Sites {info(ActiveCustomSitesTooltip)}</td>
                        {data.map(m => <td key={m.key} style={styles.dataCell}>{m.activeCustomSites}</td>)}
                    </tr>
                    <tr>
                        <td style={styles.labelCell}>Active Starter Sites {info(ActiveStarterSitesTooltip)}</td>
                        {data.map(m => <td key={m.key} style={styles.dataCell}>{m.activeStarterSites}</td>)}
                    </tr>
                    <tr>
                        <td style={styles.labelCell}>
                            New Inactive {info(NewInactiveTooltip)}
                            <NewInactiveByReasonModal isSuperAdmin={isSuperAdmin} year={year} coachId={coachId} />
                        </td>
                        {data.map(m => <td key={m.key} style={styles.dataCell}>{m.newInactive}</td>)}
                    </tr>
                    <tr>
                        <td style={styles.labelCell}>Living Donor TX** {info(LivingDonorTxTooltip)}</td>
                        {data.map(m => <td key={m.key} style={styles.dataCell}>{m.livingDonorTransplants}</td>)}
                    </tr>
                    {isSuperAdmin && (
                        <tr>
                            <td style={styles.labelCell}>New MS Registration {info(NewMsRegistrationTooltip)}</td>
                            {data.map(m => <td key={m.key} style={styles.dataCell}>{m.newMsRegistrations}</td>)}
                        </tr>
                    )}
                    {isSuperAdmin && (
                        <tr>
                            <td style={styles.labelCell}>Opt-In % {info(OptInPercentTooltip)}</td>
                            {data.map(m => (
                                <td key={m.key} style={styles.dataCell}>
                                    {m.newMsRegistrations === 0 ? '-' : `${m.optInPercent.toFixed(1)}%`}
                                </td>
                            ))}
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
        <div style={styles.footnotes}>
            <p style={styles.footnote}>* Active patients are those with a coaching status of In Process, Contact Attempted, Contact Made, or Received Voucher who have not been deactivated or hidden from the coach. Includes both Starter and Custom Site patients.</p>
            <p style={styles.footnote}>** Living Donor TX includes Living Donor Transplant and Living Donor Transplant (From Other Center) events.</p>
        </div>
        </>
    );
};

export default PerformanceGrid;
